/**
 * Редактор документа «Фінансова звітність» (ч.1386): дві форми набору з рядками, які можна
 * виправити (підсумки рахує сервер), реквізити шапки, «перевірити» (баланс, підсумки, XSD),
 * «перерахувати з обліку», друк; статус подання — з Кабінету ДПС.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FORM_SET_TITLES, FORM_TABS, PERIOD_LABELS, financialStatements, formsFor,
  type FinStatementCheck, type FinStatementFields,
} from '@/api/financialStatements';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { EquityMatrix, FinSheetHeader, FinSignatures, FinStatementForm } from './FinStatementBlank';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

export default function FinancialStatementEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [values, setValues] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<FinStatementFields>({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Вкладка — код форми документа ('1', '2', '3', '4', '1m'…) або 'HEAD' (ч.1388: за рік у повному наборі — чотири форми).
  const [tab, setTab] = useState<string>('');
  const [thousands, setThousands] = useState(true);
  const [check, setCheck] = useState<FinStatementCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const docQ = useQuery({ queryKey: ['financial-statement', id], queryFn: () => financialStatements.get(id!), enabled: !!id });
  const d = docQ.data;
  const formsQ = useQuery({ queryKey: ['financial-statement-forms'], queryFn: financialStatements.forms, staleTime: 3_600_000 });
  const subQ = useQuery({ queryKey: ['financial-statement-sub', id], queryFn: () => financialStatements.submission(id!), enabled: !!id, refetchInterval: 30_000 });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);

  useEffect(() => {
    if (d) { setValues(d.values ?? {}); setFields(d.fields ?? {}); setDirty(false); }
  }, [d]);
  // ч.584/ч.1059: незбережені правки рядків і реквізитів переживають перехід між сторінками.
  const draftValue = useMemo(() => ({ values, fields, dirty }), [values, fields, dirty]);
  const restoreDraft = useCallback((v: { values: Record<string, string>; fields: FinStatementFields; dirty: boolean }) => {
    setValues(v.values ?? {}); setFields(v.fields ?? {}); setDirty(!!v.dirty);
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, { ready: !!d, clearOnAnyMutation: false });

  const subStatus = subQ.data?.status ?? null;
  const readOnly = !!subStatus && !['DRAFT', 'ERROR'].includes(subStatus);
  const forms = useMemo(() => {
    if (!d || !formsQ.data) return null;
    return formsFor(d.form_set, d.period_quarter).map((code) => formsQ.data!.find((f) => f.form_code === code)!).filter(Boolean);
  }, [d, formsQ.data]);
  const activeTab = tab || forms?.[0]?.form_code || 'HEAD';

  const save = useMutation({
    mutationFn: () => financialStatements.update(id!, { values, fields }),
    onSuccess: () => { setDirty(false); setError(null); setCheck(null); draft.markSaved(); qc.invalidateQueries({ queryKey: ['financial-statement', id] }); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const recalc = useMutation({
    mutationFn: () => financialStatements.recalc(id!),
    onSuccess: () => { setError(null); setCheck(null); draft.markSaved(); qc.invalidateQueries({ queryKey: ['financial-statement', id] }); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => financialStatements.remove(id!),
    onSuccess: () => { draft.markSaved(); qc.invalidateQueries({ queryKey: ['financial-statements'] }); navigate('/reports/financial'); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const saveDocDate = useMutation({
    mutationFn: (ymd: string) => financialStatements.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['financial-statement', id] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try {
      if (dirty && !readOnly) await save.mutateAsync();
      setCheck(await financialStatements.check(id));
    } catch (e: any) { setError(e?.response?.data?.detail ?? String(e)); } finally { setChecking(false); }
  }
  async function openPrint() {
    if (dirty && !readOnly) await save.mutateAsync();
    navigate(`/reports/financial/${id}/print`);
  }
  const setV = (code: string, raw: string) => {
    setValues((v) => { const n = { ...v }; if (raw === '' || Number(raw) === 0) delete n[code]; else n[code] = raw; return n; });
    setDirty(true);
  };
  const setF = (patch: Partial<FinStatementFields>) => { setFields((f) => ({ ...f, ...patch })); setDirty(true); };

  if (!d) return <div className="card">Завантаження…</div>;
  const title = `Фінансова звітність — ${FORM_SET_TITLES[d.form_set]} — ${PERIOD_LABELS[d.period_quarter]} ${d.period_year}`;
  const field = (key: keyof FinStatementFields, label: string, span = 4, title?: string) => (
    <div className="field" style={{ gridColumn: `span ${span}` }}>
      <label htmlFor={`fs-${key}`} title={title}>{label}</label>
      <input id={`fs-${key}`} value={(fields[key] as string | number | undefined) ?? ''} disabled={readOnly}
        onChange={(e) => setF({ [key]: key === 'kil' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value } as Partial<FinStatementFields>)} />
    </div>
  );

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/reports/financial')}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>зберегти</button>
              <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>
              <button disabled={recalc.isPending} onClick={() => {
                confirm('Перерахувати з обліку? Ручні правки рядків буде перезаписано.')
                  .then((ok) => { if (ok) recalc.mutate(); });
              }}>перерахувати з обліку</button>
              <button className="danger" onClick={() => { confirm('Видалити фінансову звітність?').then((ok) => { if (ok) remove.mutate(); }); }}>видалити</button>
            </>
          )}
          <button onClick={openPrint} title="Друкована форма">друк</button>
        </div>
      </div>

      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
        padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
            : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
            : subStatus === 'SENT' ? '→ відправлено у ДПС'
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
            : 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }} onClick={async () => {
          if (dirty && !readOnly) await financialStatements.update(id!, { values, fields });
          navigate('/dps-cabinet');
        }}>Кабінет ДПС →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{d.document_number} · суми у гривнях з копійками, у файлі для ДПС — тис. грн · рядки можна виправити, підсумки рахуються самі.</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={thousands} onChange={(e) => setThousands(e.target.checked)} style={{ width: 'auto' }} />
          показувати тис. грн
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Дата подання (D_FILL/HFILL)">
          Дата подання
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''} onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}

      {d.warnings.length > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          <b>З розрахунку — перевірте:</b>
          <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{d.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w}</li>)}</ul>
        </div>
      )}
      {check && (
        <div style={{ marginBottom: '0.75rem' }}>
          {check.errors.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — звітність готова до подання
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Помилки ({check.errors.length}) — виправте перед поданням:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.errors.map((e, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{e.message}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        {(forms ?? []).map((f) => (
          <button key={f.form_code} className={activeTab === f.form_code ? 'active' : ''} onClick={() => setTab(f.form_code)}>
            {FORM_TABS[f.form_code] ?? f.form_code}
          </button>
        ))}
        <button className={activeTab === 'HEAD' ? 'active' : ''} onClick={() => setTab('HEAD')}>Реквізити</button>
      </div>

      {!forms && <div>Завантаження форм…</div>}
      {(forms ?? []).filter((f) => f.form_code === activeTab).map((f) => (
        <div key={f.form_code} style={{ overflowX: 'auto' }}>
          <FinSheetHeader doc={d} org={org} formCode={f.form_code} />
          {f.kind === 'matrix'
            ? <EquityMatrix form={f} values={values} setV={setV} readOnly={readOnly} thousands={thousands} />
            : <FinStatementForm form={f} doc={d} values={values} setV={setV} readOnly={readOnly} thousands={thousands} />}
          <FinSignatures org={org} />
        </div>
      ))}
      {activeTab === 'HEAD' && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Шапка бланка. Заповнюється з картки організації ({org?.name ?? ''}); тут можна виправити для цього документа.
            Керівник і головний бухгалтер — з картки організації.
          </p>
          <div className="doc-grid">
            {field('name', 'Підприємство (повна назва)', 8)}
            {field('tin', 'ЄДРПОУ', 4)}
          </div>
          <div className="doc-grid">
            {field('katottg', 'Код КАТОТТГ', 4, 'На бланку — «за КАТОТТГ»; з картки організації')}
            {field('koatuu_name', 'Територія', 8)}
          </div>
          <div className="doc-grid">
            {field('koatuu', 'Код КОАТУУ для файла ДПС', 4, 'Електронна форма ДПС досі вимагає код КОАТУУ (10 цифр): код КАТОТТГ у цьому полі вона не приймає. Підставляється з попереднього звіту організації.')}
            <div className="field" style={{ gridColumn: 'span 8', alignSelf: 'end' }}>
              <small style={{ color: 'var(--text-secondary)' }}>
                На бланку друкується КАТОТТГ, а файл для ДПС досі має поле КОАТУУ — вкажіть 10-значний код
                КОАТУУ території. Наступні звіти організації візьмуть його звідси.
              </small>
            </div>
          </div>
          <div className="doc-grid">
            {field('kopfg', 'Код КОПФГ', 4)}
            {field('kopfg_name', 'Організаційно-правова форма', 8)}
          </div>
          <div className="doc-grid">
            {field('kved', 'Код КВЕД', 4)}
            {field('kved_name', 'Вид економічної діяльності', 8)}
          </div>
          <div className="doc-grid">
            {field('kil', 'Середня кількість працівників, осіб', 4, 'З обліку: середнє штатних на кінець кожного місяця періоду')}
            {field('loc', 'Адреса', 5)}
            {field('tel', 'Телефон', 3)}
          </div>
          {d.form_set === 'FULL' && (
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Складено за</label>
                <select value={fields.standards ?? 'NPSBO'} disabled={readOnly}
                  onChange={(e) => setF({ standards: e.target.value as 'NPSBO' | 'IFRS' })}>
                  <option value="NPSBO">національними положеннями (стандартами) бухгалтерського обліку</option>
                  <option value="IFRS">міжнародними стандартами фінансової звітності</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
